import type { ReactNode } from "react";
import { BrandMark, SiteFooter } from "../components/Brand";
import { IconLock } from "../components/icons";
import { Preferences } from "../components/Preferences";
import { useBrand } from "../branding/BrandContext";
import { useI18n } from "../i18n";
import { formatDateTime } from "../lib/format";

export function PageShell({
  children,
  banner,
  narrow,
  width,
  invoiceId,
  printTitle,
}: {
  children: ReactNode;
  banner?: ReactNode;
  /** One column (status, receipt, claim pages). */
  narrow?: boolean;
  /** Explicit width: "doc" for the billing document, "form" for signup. */
  width?: "doc" | "form";
  /** Shown in the "Need help?" panel so the customer can quote it. */
  invoiceId?: string;
  /** When set, printing the page (receipt download) adds a document header with this title. */
  printTitle?: string;
}) {
  const { t, locale } = useI18n();
  const { brand } = useBrand();
  const cls = ["page", narrow ? "page-narrow" : "", width ? `page-${width}` : ""].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <div className="backdrop" aria-hidden="true">
        <span className="glow glow-1" />
        <span className="glow glow-2" />
        <span className="grain" />
      </div>
      {banner}
      <header className="topbar">
        <BrandMark />
        <div className="topbar-end">
          <span className="topbar-secure" title={t("app.title")}>
            <IconLock size={13} />
            <span className="topbar-secure-label">{t("app.title")}</span>
          </span>
          <Preferences />
        </div>
      </header>
      {printTitle ? (
        <div className="print-head" aria-hidden="true">
          <strong>{printTitle}</strong>
          <span>{t("print.issuedBy", { operator: brand.name })}</span>
          <span>{t("print.generated", { date: formatDateTime(new Date().toISOString(), locale) })}</span>
        </div>
      ) : null}
      {children}
      <SiteFooter invoiceId={invoiceId} />
    </div>
  );
}
